<template>
  <div class="relativeContainer">
    <perfect-scrollbar
      ref="scrollable"
      class="editor-container"
      :style="`height: ${height};`"
      :options="{ suppressScrollX: false }"
    >
      <v-chip v-if="isReadOnly && !error" class="read-only">
        {{ $t('reflectionForm.headers.readOnly') }}
      </v-chip>
      <div class="d-flex flex-column align-center">
        <EditorContext
          v-if="validatedVersion"
          class="editor-context-padding editor-box mb-4"
          :reflection="reflection"
        />
        <div
          v-if="loading"
          class="d-flex align-center"
        >
          <v-progress-circular indeterminate />
        </div>
        <div v-if="error" class="d-flex align-center">
          <v-card class="d-flex flex-column pa-4 mt-6 align-center text-center" width="420">
            {{ errorPanelMessage }}
            <v-btn text small class="mt-4 primary" @click="reloadPage">
              {{ $t('reflectionForm.reloadPanel.buttonCaption') }}
            </v-btn>
          </v-card>
        </div>
        <div
          v-else-if="validatedVersion"
        >
          <noos-editor
            :readonly="isReadOnly"
            @editorChange="$event => $emit('editorChange', $event.detail)"
            @editorReady="onEditorReady"
            @editorStatusChange="$event => $emit('editorStatusChange', $event.detail)"
            @outdatedSchema="outdatedSchemaCallback"
            @accessDenied="accessDeniedCallback"
          />
          <JournalExtension />
        </div>
      </div>
    </perfect-scrollbar>
    <EditorFileUploader :base-path="`/reflections/${reflection.id}`" />
    <slot name="tools" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getFirebaseAuth } from '@/shared/firebase/firebase';
import { firebaseConfig } from '../../../../../firebaseConfig';
import { types } from '../../../../../store/rootTypes';
import HttpClientWithRetry from '../../../../../shared/httpclient/httpClientWithRetry';
import { currentVersion, compareVersion } from '../../../../../tools/currentVersion';
import JournalExtension from './JournalExtension';
import EditorFileUploader from './EditorFileUploader';
import EditorContext from '../EditorContext';

const Err = {
  NETWORK: 'NETWORK_ERROR',
  VERSION: 'VERSION_ERROR',
  ACCESS_DENIED: 'ACCESS_DENIED_ERROR',
};

export default {
  name: 'EditorPage',
  components: { EditorContext, JournalExtension, EditorFileUploader },
  props: {
    reflection: {
      type: Object,
      required: true,
    },
    height: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      error: undefined,
      loading: true,
      validatedVersion: false,
    };
  },
  mounted() {
    this.$refs.scrollable.$el.addEventListener('scroll', this.onScroll);
  },
  computed: {
    ...mapGetters({
      globalConfig: types.globalConfig.getters.config,
      isReadOnly: types.reflections.getters.reflectionReadOnly,
    }),
    errorPanelMessage() {
      switch (this.error) {
        case Err.VERSION:
          return this.$t('reflectionForm.reloadPanel.message.newSchema');

        case Err.ACCESS_DENIED:
          return this.$t('reflectionForm.reloadPanel.message.accessDenied');

        case Err.NETWORK:
          return this.$t('reflectionForm.reloadPanel.message.connectionError');

        default:
          return this.$t('reflectionForm.reloadPanel.message.unknown');
      }
    },
  },
  async created() {
    try {
      if (await this.getAndValidateDocVersionFromCollabServer() === false) {
        this.outdatedSchemaCallback();
      } else {
        this.validatedVersion = true;
      }
    } catch (e) {
      console.error(e);
      this.loading = false;
      this.error = Err.NETWORK;
    }
  },
  methods: {
    reloadPage() {
      window.location.reload();
    },
    onScroll(event) {
      this.$emit('editorScroll', event);
    },
    onEditorReady(event) {
      console.log('onEditorReady', event);
      this.loading = false;
      this.$emit('editorReady', event.detail);
    },
    outdatedSchemaCallback() {
      this.loading = false;
      this.error = Err.VERSION;
    },
    accessDeniedCallback() {
      this.loading = false;
      this.error = Err.ACCESS_DENIED;
      this.validatedVersion = false;
    },
    async getAndValidateDocVersionFromCollabServer() {
      const collabServerHttpClient = new HttpClientWithRetry({
        serviceConfiguration: {
          baseUrl: `https://${this.globalConfig.collabServerUrl}`,
          requestTimeoutInMs: 20000,
        },
      });

      let queryPath = `/${this.reflection.id}/version`;
      queryPath += `?firebaseProjectId=${encodeURIComponent(firebaseConfig.projectId)}`;
      queryPath += `&firebaseIdToken=${encodeURIComponent(await getFirebaseAuth().currentUser.getIdToken(false))}`;

      // Issue a GET to the collabServer on '/:docId/version' to get the version of the
      // document currently saved in hocuspocus. If NO version is returned, it means this document
      // have never been opened since we introduced the docVersionNumber, which is fine, it will be
      // automatically saved to the current appVersion by the collabServer.
      //
      // If we got the document version and it's newer than the current appVersion, a reload is required.
      //
      // If we're not able to reach the collabServer to get the document version, error...
      try {
        const documentVersionResponse = (await collabServerHttpClient.get(queryPath));
        const documentVersion = documentVersionResponse.data.version;
        console.log('applicationVersion................. : ', currentVersion());
        console.log('documentVersion (from collabServer) : ', documentVersion);
        if (documentVersion && compareVersion(currentVersion(), documentVersion) < 0) {
          return false;
        }
      } catch (err) {
        console.error('Unable to reach the collaboration server to get the document version, please contact support');
        throw Error('Collaboration Server not reachable');
      }
      return true;
    },
  },
};
</script>

<style>
.relativeContainer {
  position: relative;
  margin-right: 48px;
}

.editor-box {
  width: 816px;
  box-shadow: 0px 2px 5px 2px #ddd;
}

.editor-container {
  box-sizing: border-box;
  padding-top: 18px;
  padding-left: 56px;
  padding-right: 16px;
  display: flex;
  justify-content: center;
}

.editor-context-padding {
  padding: 16px;
  background-color: #f1f0f5;
}

.read-only {
  position: absolute;
  right: 30px;
  top: 40px;
}
</style>
